import { useTranslation } from "react-i18next"

import type { ScriptReferenceDetails } from "@/features/marketplace/components/ScriptReferenceVideosField"

import type { PersonaBriefDto } from "../api/persona-opportunity-briefs.api"
import type { PersonaScriptDto } from "../api/persona-opportunity-scripts.api"

type PersonaScriptForReference = Pick<
  PersonaScriptDto,
  "concept" | "duration" | "presenter" | "product" | "hook" | "hooks" | "scenes"
>

/** The send dialog's script preview, built from a persona script. */
export function personaScriptReferenceDetails(
  script: PersonaScriptForReference,
): ScriptReferenceDetails {
  const hooks = [...(script.hooks ?? [])]
    .sort((a, b) => a.position - b.position)
    .map((hook) => hook.voiceover)
  return {
    concept: script.concept,
    duration: script.duration,
    presenter: script.presenter,
    product: script.product,
    hooks: hooks.length > 0 ? hooks : script.hook ? [script.hook] : [],
    scenes: [...(script.scenes ?? [])]
      .filter((scene) => !scene.deletedAt)
      .sort((a, b) => a.sceneNumber - b.sceneNumber)
      .map((scene) => ({
        number: scene.sceneNumber,
        name: scene.sceneName,
        seeing: scene.seeing,
        hearing: scene.hearing,
      })),
  }
}

type PersonaBriefForReference = Pick<
  PersonaBriefDto,
  "duration" | "hooks" | "concept" | "cta" | "creatorProfile" | "notes"
>

export interface BriefReferenceLabels {
  concept: string
  cta: string
  creator: string
  notes: string
}

/** Direction, then the spoken line in quotes, skipping whichever is blank. */
function directedLine(part: { direction: string; line: string }): string {
  const line = part.line.trim()
  return [part.direction.trim(), line ? `“${line}”` : ""].filter(Boolean).join("\n")
}

/** The send dialog's preview for a brief: hooks, then concept, CTA, creator and notes. */
export function personaBriefReferenceDetails(
  brief: PersonaBriefForReference,
  labels: BriefReferenceLabels,
): ScriptReferenceDetails {
  const creator = [
    brief.creatorProfile.ageRange,
    brief.creatorProfile.gender,
    brief.creatorProfile.preferences,
  ]
    .map((part) => part?.trim() ?? "")
    .filter(Boolean)
    .join(" · ")
  return {
    duration: brief.duration,
    hooks: [...brief.hooks]
      .sort((a, b) => a.hookIndex - b.hookIndex)
      .map((hook) => hook.line),
    notes: [
      { label: labels.concept, text: directedLine(brief.concept) },
      { label: labels.cta, text: directedLine(brief.cta) },
      { label: labels.creator, text: creator },
      { label: labels.notes, text: brief.notes ?? "" },
    ],
  }
}

/** Brief preview labels in the current language. */
export function useBriefReferenceLabels(): BriefReferenceLabels {
  const { t } = useTranslation()
  return {
    concept: t("marketplace.sendHandoff.references.briefConcept", "Concept"),
    cta: t("marketplace.sendHandoff.references.briefCta", "Call to action"),
    creator: t("marketplace.sendHandoff.references.briefCreator", "Creator"),
    notes: t("marketplace.sendHandoff.references.briefNotes", "Notes"),
  }
}
